import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Palette, Users, Download, CreditCard, MessageSquare } from 'lucide-react';

const ServicesPage = () => {
  const services = [
    {
      icon: BookOpen,
      title: "Comic Hosting",
      description: "Host your webcomics for free with unlimited episodes. Reach readers worldwide with our optimized platform.",
      features: ["Unlimited uploads", "Vertical scroll reader", "Episode management", "Analytics dashboard"],
      color: "primary"
    },
    {
      icon: CreditCard,
      title: "Artist Subscriptions",
      description: "Support your favorite artists with weekly or monthly subscriptions. Get exclusive access to premium content.",
      features: ["Flexible pricing", "Auto-renewal", "Cancel anytime", "Exclusive content"],
      color: "secondary"
    },
    {
      icon: Palette,
      title: "Fan Art Marketplace",
      description: "Commission talented artists to complete your comic pages or create custom artwork.",
      features: ["Secure payments", "Artist selection", "Progress tracking", "Quality guarantee"],
      color: "accent"
    },
    {
      icon: Download,
      title: "PDF Export",
      description: "Organize your comic pages and export them as printable PDF files for physical printing.",
      features: ["High-quality export", "Page organization", "Print-ready format", "Multiple layouts"],
      color: "primary"
    },
    {
      icon: Users,
      title: "Community Features",
      description: "Engage with readers through comments, likes, and shares. Build your fanbase.",
      features: ["Comments", "Likes & favorites", "Social sharing", "Reader analytics"],
      color: "secondary"
    },
    {
      icon: MessageSquare,
      title: "Blog Platform",
      description: "Share updates, tutorials, and news with your audience through our integrated blog.",
      features: ["Rich text editor", "Image support", "Comment moderation", "SEO optimized"],
      color: "accent"
    }
  ];

  return (
    <div className="min-h-screen pb-24 pt-8 page-padding" data-testid="services-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Services</h1>
        <p className="text-xl text-text-secondary mb-12 max-w-2xl">
          Everything you need to create, share, and monetize your comics
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div 
              key={index} 
              className="card-hover p-6 flex flex-col"
              data-testid={`service-${index}`}
            >
              <service.icon 
                className={`mb-4 ${
                  service.color === 'primary' ? 'text-primary' :
                  service.color === 'secondary' ? 'text-secondary' :
                  'text-accent'
                }`} 
                size={40} 
              />
              <h3 className="text-xl font-bold mb-2">{service.title}</h3>
              <p className="text-text-secondary mb-4 flex-1">{service.description}</p>
              <ul className="space-y-2">
                {service.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Pricing */}
        <section className="mt-20">
          <h2 className="text-3xl font-bold text-center mb-12">Simple Pricing</h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <div className="card-hover p-8">
              <h3 className="text-xl font-bold mb-2">Free</h3>
              <p className="text-4xl font-bold text-primary mb-4">$0<span className="text-sm text-text-secondary">/forever</span></p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-success rounded-full" />
                  Read all free comics
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-success rounded-full" />
                  Upload your own comics
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-success rounded-full" />
                  Comment and interact
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-success rounded-full" />
                  Basic analytics
                </li>
              </ul>
              <Link to="/signup" className="btn-neo w-full text-center block">
                Get Started
              </Link>
            </div>

            <div className="card-hover p-8 border-primary">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-bold">Subscriptions</h3>
                <span className="badge-up">Popular</span>
              </div>
              <p className="text-4xl font-bold text-primary mb-4">From $2.99<span className="text-sm text-text-secondary">/week</span></p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-success rounded-full" />
                  Access premium content
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-success rounded-full" />
                  Support your favorite artists
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-success rounded-full" />
                  Early episode access
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-success rounded-full" />
                  Exclusive content
                </li>
              </ul>
              <Link to="/artists" className="btn-neo w-full text-center block">
                Find Artists
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="mt-20">
          <div className="bg-surface border border-border p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to Start?</h2>
            <p className="text-text-secondary mb-6 max-w-xl mx-auto">
              Join thousands of creators and millions of readers on the fastest growing comic platform.
            </p>
            <Link to="/signup" className="btn-neo">
              Create Free Account
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ServicesPage;
